import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const Row = ({ line, token, onDone }) => {
  const navigate = useNavigate();
  const [amount, setAmount] = useState(line.requested_amount_usd);
  const decide = async (action) => {
    try {
      await axios.post(`${API}/pas-banking/admin/credit-lines/${line.credit_line_id}/${action}`,
        action === 'approve' ? { approved_amount_usd: parseFloat(amount) } : { note: 'Declined by administrator' },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Credit line ${action === 'approve' ? 'approved' : 'rejected'}`);
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || 'Action failed'); }
  };
  return (
    <tr className="hover:bg-slate-700/50" data-testid={`credit-line-row-${line.credit_line_id}`}>
      <td className="px-4 py-3"><p className="text-white">{line.client_name}</p><p className="text-slate-400 text-xs">{line.client_email}</p></td>
      <td className="px-4 py-3 text-white">{usd(line.requested_amount_usd)}<p className="text-slate-500 text-xs">{line.purpose}</p></td>
      <td className="px-4 py-3 text-slate-300 text-sm">{line.status === 'approved' ? `${usd(line.used_usd)} of ${usd(line.approved_amount_usd)}` : '-'}
        {line.status === 'approved' && <p className="text-xs text-slate-500" data-testid={`credit-line-billing-${line.credit_line_id}`}>{line.repayment_exempt ? 'Exempt from repayment' : line.agreement ? `Signed · next ${line.next_payment_due || '-'}` : 'Agreement not signed'}{line.past_due_usd ? ` · past due ${usd(line.past_due_usd)}` : ''}</p>}
      </td>
      <td className="px-4 py-3"><span className="text-xs uppercase text-slate-300" data-testid={`credit-line-status-${line.credit_line_id}`}>{line.status}</span></td>
      <td className="px-4 py-3 text-right">
        {['approved', 'paid_off'].includes(line.status) && <button onClick={() => navigate(`/pas/admin/credit-agreement/${line.credit_line_id}`)} data-testid={`credit-line-agreement-${line.credit_line_id}`} className="px-3 py-1 bg-cyan-500/20 text-cyan-300 rounded text-sm">Agreement</button>}
        {line.status === 'pending' && (
          <div className="flex gap-2 justify-end">
            <input type="number" value={amount} max="50000000" onChange={(e) => setAmount(e.target.value)} className="w-36 bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-sm" data-testid={`credit-line-approve-amount-${line.credit_line_id}`} />
            <button onClick={() => decide('approve')} className="px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded text-sm" data-testid={`credit-line-approve-${line.credit_line_id}`}>Approve</button>
            <button onClick={() => decide('reject')} className="px-3 py-1 bg-red-500/20 text-red-400 rounded text-sm" data-testid={`credit-line-reject-${line.credit_line_id}`}>Reject</button>
          </div>
        )}
      </td>
    </tr>
  );
};

export const AdminCreditLines = ({ token }) => {
  const [lines, setLines] = useState([]);
  const load = useCallback(async () => {
    const res = await axios.get(`${API}/pas-banking/admin/credit-lines`, { headers: { Authorization: `Bearer ${token}` } });
    setLines(res.data.credit_lines);
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const runBilling = async () => {
    try {
      const r = await axios.post(`${API}/pas-banking/admin/credit-billing/run`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Billing run: ${r.data.processed.length} payment(s) processed`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Billing run failed'); }
  };

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden" data-testid="admin-credit-lines">
      <div className="p-4 border-b border-slate-700 flex items-center justify-between">
        <p className="text-slate-400 text-sm">20-year agreements &middot; 1% interest-only &middot; auto-debited in BETH at 1 BETH = $1.00 USD (runs daily)</p>
        <button onClick={runBilling} data-testid="admin-run-billing-btn" className="px-3 py-1.5 bg-purple-500/20 text-purple-300 rounded text-sm hover:bg-purple-500/30">Collect due payments now</button>
      </div>
      <table className="w-full">
        <thead className="bg-slate-900">
          <tr>{['Client', 'Requested', 'Usage', 'Status', ''].map(h => <th key={h} className="text-left text-slate-400 text-sm font-medium px-4 py-3">{h}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-slate-700">
          {lines.map(l => <Row key={l.credit_line_id} line={l} token={token} onDone={load} />)}
          {lines.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">No credit line requests yet</td></tr>}
        </tbody>
      </table>
    </div>
  );
};
